<template>
    <main class="inventory-page__content">
        <VInventoryHeader></VInventoryHeader>

        <VContentHeaderDefault
            :title="'Справочник ' + $route.params.type"
            :button="canCreateValue ? 'Добавить' : ''"
            @search="onSearch"
            @button="onCreateButtonClick"
        ></VContentHeaderDefault>

        <!-- Отсутствие кнопки объясняется прямо здесь: без пояснения пропавшее действие
             читается как поломка, и его начинают искать. -->
        <p class="inventory-page__notice" v-if="isHiddenDictionary">{{ hiddenDictionaryNotice }}</p>

        <template v-if="isLoading">
            <div class="inventory-page__loader">
                <VSpinner></VSpinner>
            </div>
        </template>

        <template v-else>
            <VContentTableDefault
                :rows="rows"
                :page="page"
                :total="total"
                :options="options"
                @page="onPageChange"
                @edit="onInventoryClick"
                @delete="onDeleteClick"
                @click="onInventoryClick"
            ></VContentTableDefault>
            <p class="inventory-page__empty" v-if="!rows.length">Значений не найдено.</p>
        </template>

        <VInventoryAttributeModal :cache="{}"></VInventoryAttributeModal>
    </main>
</template>
<script>
// Components
import VSpinner from '../../components/VProgress/VSpinner.vue';
import PagePaginate from '../../components/PagePaginate/PagePaginate.vue';
import VContentTableDefault from '../../components/VContentTableDefault.vue';
import VContentHeaderDefault from '../../components/VContentHeaderDefault.vue';
import VInventoryHeader from './components/VInventoryHeader.vue';
import VInventoryAttributeModal from './modals/VInventoryAttributeModal.vue';
import './InventoryPage.scss';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { errorMessage } from '../../../js/error';
import {
    paginateInventoryAttributes,
    createInventoryAttribute,
    updateInventoryAttribute,
    deleteInventoryAttribute,
} from '../../../js/inventory-api';
import { Watch } from 'vue-property-decorator';
import ModalNames from '../../../js/enums/ModalNames';
import { SYSTEM_ATTRIBUTE, isHiddenDictionaryType, isSystemAttributeType } from '../../../js/inventoryAttributes';

@Component({
    components: {
        VSpinner,
        PagePaginate,
        VContentTableDefault,
        VContentHeaderDefault,
        VInventoryHeader,
        VInventoryAttributeModal,
    },
})
class InventoryPageAttributesWithType extends Vue {
    rows = [];
    page = 1;
    total = 1;
    isLoading = true;
    searchString = '';
    /**
     * Колонки и действия строки.
     *
     * КНОПКА УДАЛЕНИЯ ЕСТЬ ТОЛЬКО У СВОБОДНОГО ЗНАЧЕНИЯ. Занятое сервер удалить не даст
     * (InventoryAttributeService::delete): внешний ключ у строк оборудования стоит
     * на SET NULL, и удаление молча оборвало бы связь — значение осталось бы текстом,
     * но перестало обновляться вместе со справочником.
     *
     * Кнопка, которая всегда отвечает отказом, хуже отсутствующей, поэтому она прячется;
     * а чтобы её отсутствие не выглядело загадкой, рядом стоит колонка «Используется»
     * с числом. Решение всё равно за сервером: список мог устареть за время работы.
     *
     * Вычисляемое, а не поле класса: право правки приходит миксином из хранилища,
     * а поля класса считаются до того, как миксины появятся.
     *
     * СУММА size НАМЕРЕННО МЕНЬШЕ СТА. Колонку действий VContentTableDefault рисует
     * из остатка (см. defaultColumnSize): при сумме ровно 100 остаток равен нулю, и она
     * не появляется вовсе — вместе с кнопками. Здесь оставлено 10 на действия.
     */
    get options() {
        return {
            editable: this.$_canManageInventory,
            deletable: (row) => this.$_canManageInventory && !this.usageCount(row),
            columns: [
                {
                    size: 65,
                    label: 'Значение',
                    display: (x) => x.value,
                },
                {
                    size: 25,
                    label: 'Используется',
                    display: (x) => (this.usageCount(x) ? `${this.usageCount(x)}` : 'нет'),
                },
            ],
        };
    }

    /**
     * Справочник, значения которого заводить больше нельзя (7-D3).
     *
     * Страница остаётся доступной по прямому адресу: накопленные значения надо видеть
     * и править — их читают карточки старой техники. Закрыто именно ЗАВЕДЕНИЕ нового,
     * и сервер отказывает в нём независимо от интерфейса (InventoryAttributeService).
     */
    get isHiddenDictionary() {
        return isHiddenDictionaryType(this.$route.params.type);
    }

    get canCreateValue() {
        return this.$_canManageInventory && !this.isHiddenDictionary;
    }

    /**
     * Почему заводить нельзя. Названо своим именем, а не общей фразой: человек пришёл
     * сюда с конкретным намерением, и ему нужно знать, где это делается теперь.
     */
    get hiddenDictionaryNotice() {
        if (isSystemAttributeType(this.$route.params.type, SYSTEM_ATTRIBUTE.LOCATION_CITY)) {
            return (
                'Город оборудования хранится собственным полем единицы и выбирается из общего справочника городов. ' +
                'Значения этого справочника остались от прежней схемы, и новых не заводится.'
            );
        }

        return (
            'Серийный номер — значение конкретной единицы оборудования, а не справочник. ' +
            'Он вводится в форме создания и хранится у самой единицы.'
        );
    }

    /**
     * Сколько раз значение проставлено оборудованию и шаблонам.
     *
     * Приходит с бэкенда одним числом (usage_count). Отсутствие ключа считаем нулём,
     * а не «неизвестно»: единственное, на что оно влияет здесь, — показывать ли кнопку,
     * и отказ сервера остаётся последним словом в любом случае.
     *
     * @param {Object} attribute
     * @returns {number}
     */
    usageCount(attribute) {
        return Number(attribute?.usage_count ?? 0);
    }

    onSearch(searchString) {
        this.page = 1;
        this.searchString = searchString;
    }

    onPageChange(page) {
        this.page = page;
    }

    async onInventoryClick(attribute) {
        // Без права правки строка не открывается: здесь нет режима просмотра,
        // и форма всё равно упала бы 403 при сохранении. Для наблюдателя страница
        // остаётся списком.
        if (!this.$_canManageInventory) {
            return;
        }

        try {
            const result = await this.$showModal(ModalNames.INVENTORY_ATTRIBUTE_CARD, {
                attribute: {
                    ...attribute,
                },
                editable: {
                    value: true,
                },
                visibleFields: {
                    value: true,
                },
            });

            this.isLoading = true;

            try {
                await updateInventoryAttribute(result);
                await this.fetchInventoryAttributes();
            } catch (error) {
                let message = errorMessage(error);

                this.$showModal(ModalNames.ERROR, { errorMessage: message });
            } finally {
                this.isLoading = false;
            }
        } catch (error) {
            // The modal was closed.
            return;
        }
    }

    /**
     * Удалить значение справочника.
     *
     * Кнопка показывается только у свободного значения (см. options), поэтому
     * подтверждение говорит о последствиях самого удаления, а не о зависимостях.
     *
     * @param {Object} attribute
     */
    onDeleteClick(attribute) {
        if (!this.$_canManageInventory || this.usageCount(attribute)) {
            return;
        }

        this.$showModal(ModalNames.CONFIRM, {
            title: 'Удаление значения',
            text: `Удалить значение «${attribute.value}» из справочника? Его больше нельзя будет выбрать в карточке оборудования.`,
            cancelTitle: 'Отмена',
            confirmTitle: 'Удалить',
            confirmAction: () => this.deleteAttribute(attribute),
        });
    }

    /**
     * @param {Object} attribute
     */
    async deleteAttribute(attribute) {
        this.isLoading = true;

        try {
            await deleteInventoryAttribute(attribute.id);
            await this.fetchInventoryAttributes();
        } catch (error) {
            // Сюда приходит и отказ «значение используется»: список мог устареть,
            // пока страница была открыта. Текст с числом ссылок даёт сервер.
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(error) });
        } finally {
            this.isLoading = false;
        }
    }

    async onCreateButtonClick() {
        // Вторая застава помимо скрытой кнопки: вызов мимо интерфейса тоже не проходит.
        if (!this.canCreateValue) {
            return;
        }

        const valueType = this.$route.query.value_type;
        // Не Boolean(): из адресной строки значение приходит строкой, а Boolean('false')
        // это true. Пока флаг не доходил до бэкенда, ошибка была невидимой; теперь она
        // помечала бы участвующими в поиске значения справочников, которые в нём не нужны.
        const searchable = this.$route.query.searchable === true || this.$route.query.searchable === 'true';

        if (!valueType) {
            return;
        }

        try {
            const attribute = await this.$showModal(ModalNames.INVENTORY_ATTRIBUTE_CARD, {
                attribute: {
                    type: this.$route.params.type,
                    value: '',
                    value_type: valueType,
                    searchable: searchable,
                },
                editable: {
                    value: true,
                },
                visibleFields: {
                    value: true,
                },
            });

            this.isLoading = true;

            try {
                await createInventoryAttribute(attribute);
                await this.fetchInventoryAttributes();
            } catch (error) {
                let message = errorMessage(error);

                this.$showModal(ModalNames.ERROR, { errorMessage: message });
            } finally {
                this.isLoading = false;
            }
        } catch (error) {
            // The modal was closed.
            return;
        }
    }

    @Watch('page')
    @Watch('searchString')
    async fetchInventoryAttributes() {
        this.isLoading = true;

        try {
            const response = await paginateInventoryAttributes(this.page, this.searchString, 15, {
                whereType: this.$route.params.type,
            });

            this.rows = response.data.data;
            this.total = response.data.total;
        } catch (error) {
            const message = errorMessage(error);

            this.$showModal(ModalNames.ERROR, { errorMessage: message });
        } finally {
            this.isLoading = false;
        }
    }

    created() {
        this.fetchInventoryAttributes();
    }
}

export default InventoryPageAttributesWithType;
</script>
<style lang="scss"></style>
